import { Box, BoxProps } from '@chakra-ui/react';
import { useDrag } from '@use-gesture/react';
import { useState, useCallback, useMemo } from 'react';
import { Point, Point2 } from '../../../domain/geometry/geometric-types';
import { projectInDOM, useCameraChangedEvent } from './util';

type VertexProps = BoxProps & {
  point: Point;
  viewer: Autodesk.Viewing.Viewer3D;
  theme?: 'default' | 'inverted';
  isSelected?: boolean;
};

export const Vertex = ({
  point,
  viewer,
  theme = 'default',
  isSelected = false,
  ...boxProps
}: VertexProps) => {
  // Just use an empty object so we can notify the component when the camera changes

  const [cameraPosition, setCameraPosition] = useState({});
  const position2D = useMemo<Point2>(
    () => projectInDOM(point, viewer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [point, viewer, cameraPosition]
  );

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  const useInvertedTheme = isSelected || theme === 'inverted';
  const size = isSelected ? 12 : 10;
  const halfSize = size / 2;
  const borderWidth = '2px';

  return (
    <Box
      position="absolute"
      zIndex={1}
      // Placed in style for performance reasons
      style={{
        left: position2D[0] - halfSize,
        top: position2D[1] - halfSize,
        userSelect: 'none',
      }}
      width={`${size}px`}
      height={`${size}px`}
      borderRadius={'50%'}
      {...(useInvertedTheme
        ? { borderColor: 'white', backgroundColor: 'cyan.500' }
        : { borderColor: 'cyan.500', backgroundColor: 'white' })}
      borderWidth={borderWidth}
      onMouseDown={(event) => {
        // Prevent text selection in the UI when clicking/shift-clicking vertices
        event.preventDefault();
      }}
      {...boxProps}
    ></Box>
  );
};

type DraggableVertexProps = VertexProps & {
  onVertexDrag?: (event: MouseEvent) => void;
};

export const DraggableVertex = ({
  onVertexDrag,
  ...rest
}: DraggableVertexProps) => {
  const bindDrag = useDrag<MouseEvent>(
    (state) => {
      if (!onVertexDrag) {
        return;
      }
      // Only treat as a drag when movement is intentional (past threshold),
      // so simple clicks used for selection don't move the vertex.
      if (state.intentional) {
        onVertexDrag(state.event);
      }
    },
    {
      filterTaps: true,
      delay: true,
      // Require a few pixels of movement before starting a drag to avoid
      // tiny "jumps" when the user is just clicking to select.
      threshold: 5,
    }
  );
  return (
    <Vertex
      cursor={'move'}
      _hover={{
        filter: 'brightness(1.3)',
      }}
      {...bindDrag()}
      {...rest}
    />
  );
};
